import moment from 'moment'

import {
  addWorksheet,
  createWorkbook,
  currencyFormat,
  DEFAULTNUMHEADERS,
  numberFormat,
  saveWorkbook,
  setColumnsAndRows,
  setHeaders,
  styleTotalRow,
} from '../../utilities/helpers'

export function getFeeColumnLabel(summary) {
  const feePercent = summary?.tmFeePercent
  return feePercent != null ? `BT FEE ${feePercent.toFixed(2)}%` : 'BT FEE'
}

function sanitizeFilename(name) {
  return (
    String(name || 'dynamic-pricing-settlement')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase() || 'dynamic-pricing-settlement'
  )
}

export async function exportDynamicPricingSettlementExcel({
  data,
  organizationName = '',
  isPreview = false,
}) {
  if (!data?.rows?.length) return

  const reportTitle = isPreview
    ? 'Projected Dynamic Pricing Settlement'
    : 'Dynamic Pricing Settlement'

  const event = data.event
  const venue = event?.venue
  const venueLine = [venue?.name, venue?.city, venue?.state]
    .filter(Boolean)
    .join(' • ')

  const feeColumnLabel = getFeeColumnLabel(data.summary)

  const headerLines = [
    event?.name ? `Event: ${event.name}` : null,
    event?.start
      ? `Date / Time: ${moment(event.start).format('DD MMM YYYY h:mm A')}`
      : null,
    venueLine ? `Venue: ${venueLine}` : null,
    event?.reportCode ? `Report Code: ${event.reportCode}` : null,
    isPreview
      ? 'Estimated from preview prices and pricing level snapshots.'
      : null,
  ].filter(Boolean)

  const workbook = createWorkbook()
  const worksheet = addWorksheet('Settlement', workbook)

  setHeaders(reportTitle, organizationName, worksheet, headerLines)

  const headerRows = DEFAULTNUMHEADERS + headerLines.length

  const columns = [
    'price_level',
    'pricing_level',
    'face_value',
    'qty',
    'total_face_value',
    'total_dynamic',
    'lift',
    'bt_fee',
    'net_lift',
  ]

  const formatRow = (row) => ({
    price_level: row.priceLevel ?? row.price_level ?? '',
    pricing_level: row.pricingLevelName ?? row.pricing_level ?? '',
    face_value: Number(row.faceValue ?? row.face_value ?? 0),
    qty: Number(row.qty ?? 0),
    total_face_value: Number(row.totalFaceValue ?? row.total_face_value ?? 0),
    total_dynamic: Number(row.totalPlatinum ?? row.total_dynamic ?? 0),
    lift: Number(row.lift ?? 0),
    bt_fee: Number(row.tmFee ?? row.bt_fee ?? 0),
    net_lift: Number(row.netLift ?? row.net_lift ?? 0),
  })

  const totals = data.totals || {}
  const totalRow = {
    price_level: '',
    pricing_level: 'Total',
    face_value: '',
    qty: Number(totals.qty ?? 0),
    total_face_value: Number(totals.totalFaceValue ?? 0),
    total_dynamic: Number(totals.totalPlatinum ?? 0),
    lift: Number(totals.totalLift ?? 0),
    bt_fee: Number(totals.totalTmFee ?? 0),
    net_lift: Number(totals.totalNetLift ?? 0),
  }

  setColumnsAndRows(columns, [...data.rows, totalRow], formatRow, worksheet)

  const headerRow = worksheet.getRow(headerRows)
  ;[
    'Price Level',
    'Pricing Level',
    'Face Value',
    'Qty',
    'Total Face Value',
    'Total Dynamic',
    'Lift',
    feeColumnLabel,
    'Net Lift',
  ].forEach((label, idx) => {
    headerRow.getCell(idx + 1).value = label
  })

  numberFormat(4, worksheet, headerRows)
  currencyFormat(3, worksheet, headerRows)
  currencyFormat(5, worksheet, headerRows)
  currencyFormat(6, worksheet, headerRows)
  currencyFormat(7, worksheet, headerRows)
  currencyFormat(8, worksheet, headerRows)
  currencyFormat(9, worksheet, headerRows)

  styleTotalRow(worksheet.lastRow, worksheet, false)

  worksheet.addRow([])
  const summaryRows = [
    ['Dynamic Pricing Total', Number(data.summary?.platinumTotal ?? 0)],
    [feeColumnLabel, -Number(data.summary?.tmFeeAmount ?? 0)],
    [
      'Total Payment: Net Dynamic Amount (Face Value + Net Lift)',
      Number(data.summary?.netPlatinumAmount ?? 0),
    ],
  ]

  summaryRows.forEach(([label, value]) => {
    const row = worksheet.addRow([label, value])
    row.getCell(2).numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00'
    row.getCell(1).font = { bold: label.startsWith('Total Payment') }
    if (label.startsWith('Total Payment')) {
      row.getCell(2).font = { bold: true }
    }
  })

  const fileBase = isPreview
    ? `projected-dynamic-pricing-settlement-${sanitizeFilename(event?.name)}`
    : `dynamic-pricing-settlement-${sanitizeFilename(event?.name)}`

  await saveWorkbook(workbook, fileBase)
}
